import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  message,
} from 'antd';
import { SearchOutlined, DownloadOutlined } from '@ant-design/icons';
import { Navigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { equipeService, relatorioService, unidadeService } from '../services/api';
import { usePermissaoRecurso } from '../hooks/usePermissoes';
import { downloadCsv } from '../utils/csv';
import { formatCPF, onlyDigits } from '../utils/documento';

const EXPORT_PAGE_SIZE = 200;

// --- Formatação de apresentação (tela) -------------------------------------
// Retornam '-' para valores ausentes; usadas apenas em `render` das colunas.

const formatarTelefoneExibicao = (value) => {
  const digits = onlyDigits(value);
  if (!digits) return '-';
  if (digits.length === 11) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  // Quantidade de dígitos não reconhecida com segurança: preserva o valor original.
  return String(value ?? '') || '-';
};

const formatarDataExibicao = (value) => (value ? dayjs(value).format('DD/MM/YYYY') : '-');

const formatarMoedaExibicao = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const numero = Number(value);
  if (Number.isNaN(numero)) return '-';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(numero);
};

const formatarPercentualExibicao = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const numero = Number(value);
  if (Number.isNaN(numero)) return '-';
  return `${numero.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
};

const formatarTextoExibicao = (value) => {
  const texto = typeof value === 'string' ? value.trim() : value;
  return texto ? String(value) : '-';
};

const capitalizarPalavraExibicao = (value) => {
  const texto = typeof value === 'string' ? value.trim() : '';
  if (!texto) return '-';
  return texto.charAt(0).toUpperCase() + texto.slice(1).toLowerCase();
};

// --- Formatação para CSV ----------------------------------------------------
// Retornam string vazia (nunca '-') para valores ausentes, conforme regra de
// não representar nulo como dado no CSV.

const paraCelulaTexto = (value) => {
  const texto = typeof value === 'string' ? value.trim() : value;
  return texto ? String(value) : '';
};

const paraCelulaData = (value) => (value ? dayjs(value).format('DD/MM/YYYY') : '');

const paraCelulaInteiro = (value) => (value === null || value === undefined || value === '' ? '' : String(value));

const paraCelulaMoeda = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const numero = Number(value);
  if (Number.isNaN(numero)) return '';
  return numero.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const paraCelulaPercentual = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const numero = Number(value);
  if (Number.isNaN(numero)) return '';
  return numero.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

const paraCelulaTelefone = (value) => {
  const digits = onlyDigits(value);
  if (!digits) return '';
  if (digits.length === 11) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  return String(value ?? '');
};

const paraCelulaCep = (value) => {
  const digits = onlyDigits(value);
  if (digits.length !== 8) return paraCelulaTexto(value);
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
};

const RelatoriosOperacionais = () => {
  const { loading: permissaoLoading, isAdmin, podeVisualizar } = usePermissaoRecurso('relatorios_operacionais');

  const [loadingAlunos, setLoadingAlunos] = useState(false);
  const [loadingTurmas, setLoadingTurmas] = useState(false);
  const [loadingMatriculas, setLoadingMatriculas] = useState(false);
  const [loadingProfessores, setLoadingProfessores] = useState(false);
  const [unidades, setUnidades] = useState([]);
  const [professores, setProfessores] = useState([]);
  const [filtros, setFiltros] = useState({
    id_unidade: null,
    id_professor: null,
    ano_letivo: null,
    data_referencia: dayjs(),
  });

  const [alunosAtivos, setAlunosAtivos] = useState([]);
  const [turmasAbertas, setTurmasAbertas] = useState([]);
  const [matriculasAtivas, setMatriculasAtivas] = useState([]);
  const [professoresSimples, setProfessoresSimples] = useState([]);

  const [alunosPagination, setAlunosPagination] = useState({
    page: 1,
    page_size: 20,
    total: 0,
    total_pages: 0,
  });
  const [turmasPagination, setTurmasPagination] = useState({
    page: 1,
    page_size: 20,
    total: 0,
    total_pages: 0,
  });
  const [matriculasPagination, setMatriculasPagination] = useState({
    page: 1,
    page_size: 20,
    total: 0,
    total_pages: 0,
  });
  const [professoresPagination, setProfessoresPagination] = useState({
    page: 1,
    page_size: 20,
    total: 0,
    total_pages: 0,
  });
  const [exporting, setExporting] = useState({
    alunos: false,
    turmas: false,
    matriculas: false,
    professores: false,
  });

  useEffect(() => {
    const fetchFiltros = async () => {
      try {
        const [unidadesResponse, professoresResponse] = await Promise.all([
          unidadeService.list(),
          equipeService.list({ status_filter: 'ATIVO' }),
        ]);

        setUnidades(Array.isArray(unidadesResponse.data) ? unidadesResponse.data : []);
        setProfessores(Array.isArray(professoresResponse.data) ? professoresResponse.data : []);
      } catch (error) {
        console.error('Erro ao carregar filtros de relatórios operacionais', error);
      }
    };

    fetchFiltros();
  }, []);

  const getBaseParams = (filtrosAtuais = filtros) => {
    const params = {
      data_referencia: dayjs(filtrosAtuais.data_referencia).format('YYYY-MM-DD'),
    };
    if (filtrosAtuais.id_unidade) {
      params.id_unidade = filtrosAtuais.id_unidade;
    }
    if (filtrosAtuais.id_professor) {
      params.id_professor = filtrosAtuais.id_professor;
    }
    if (filtrosAtuais.ano_letivo) {
      params.ano_letivo = filtrosAtuais.ano_letivo;
    }
    return params;
  };

  const buscarAlunosAtivos = async (
    page = 1,
    pageSize = alunosPagination.page_size,
    filtrosAtuais = filtros
  ) => {
    setLoadingAlunos(true);
    try {
      const response = await relatorioService.alunosAtivos({
        ...getBaseParams(filtrosAtuais),
        page,
        page_size: pageSize,
      });
      const payload = response.data || {};
      setAlunosAtivos(Array.isArray(payload.items) ? payload.items : []);
      setAlunosPagination(payload.pagination || {
        page,
        page_size: pageSize,
        total: 0,
        total_pages: 0,
      });
    } catch (error) {
      message.error('Erro ao carregar relatório de alunos ativos.');
      console.error(error);
    } finally {
      setLoadingAlunos(false);
    }
  };

  const buscarTurmasAbertas = async (
    page = 1,
    pageSize = turmasPagination.page_size,
    filtrosAtuais = filtros
  ) => {
    setLoadingTurmas(true);
    try {
      const response = await relatorioService.turmasAbertas({
        ...getBaseParams(filtrosAtuais),
        page,
        page_size: pageSize,
      });
      const payload = response.data || {};
      setTurmasAbertas(Array.isArray(payload.items) ? payload.items : []);
      setTurmasPagination(payload.pagination || {
        page,
        page_size: pageSize,
        total: 0,
        total_pages: 0,
      });
    } catch (error) {
      message.error('Erro ao carregar relatório de turmas abertas.');
      console.error(error);
    } finally {
      setLoadingTurmas(false);
    }
  };

  const buscarMatriculasAtivas = async (
    page = 1,
    pageSize = matriculasPagination.page_size,
    filtrosAtuais = filtros
  ) => {
    setLoadingMatriculas(true);
    try {
      const response = await relatorioService.matriculasAtivasOperacional({
        ...getBaseParams(filtrosAtuais),
        page,
        page_size: pageSize,
      });
      const payload = response.data || {};
      setMatriculasAtivas(Array.isArray(payload.items) ? payload.items : []);
      setMatriculasPagination(payload.pagination || {
        page,
        page_size: pageSize,
        total: 0,
        total_pages: 0,
      });
    } catch (error) {
      message.error('Erro ao carregar relatório de matrículas ativas.');
      console.error(error);
    } finally {
      setLoadingMatriculas(false);
    }
  };

  const buscarProfessoresSimples = async (
    page = 1,
    pageSize = professoresPagination.page_size,
    filtrosAtuais = filtros
  ) => {
    setLoadingProfessores(true);
    try {
      const response = await relatorioService.professoresSimples({
        ...getBaseParams(filtrosAtuais),
        page,
        page_size: pageSize,
      });
      const payload = response.data || {};
      setProfessoresSimples(Array.isArray(payload.items) ? payload.items : []);
      setProfessoresPagination(payload.pagination || {
        page,
        page_size: pageSize,
        total: 0,
        total_pages: 0,
      });
    } catch (error) {
      message.error('Erro ao carregar relatório simples de professores.');
      console.error(error);
    } finally {
      setLoadingProfessores(false);
    }
  };

  const handleBuscar = async () => {
    await Promise.all([
      buscarAlunosAtivos(1, alunosPagination.page_size, filtros),
      buscarTurmasAbertas(1, turmasPagination.page_size, filtros),
      buscarMatriculasAtivas(1, matriculasPagination.page_size, filtros),
      buscarProfessoresSimples(1, professoresPagination.page_size, filtros),
    ]);
  };

  const handleLimparFiltros = async () => {
    const filtrosLimpos = {
      id_unidade: null,
      id_professor: null,
      ano_letivo: null,
      data_referencia: dayjs(),
    };

    setFiltros(filtrosLimpos);

    await Promise.all([
      buscarAlunosAtivos(1, alunosPagination.page_size, filtrosLimpos),
      buscarTurmasAbertas(1, turmasPagination.page_size, filtrosLimpos),
      buscarMatriculasAtivas(1, matriculasPagination.page_size, filtrosLimpos),
      buscarProfessoresSimples(1, professoresPagination.page_size, filtrosLimpos),
    ]);
  };

  useEffect(() => {
    handleBuscar();
  }, []);

  const fetchAllPages = async (serviceFn, filtrosAtuais) => {
    const allItems = [];
    let page = 1;
    let totalPages = 1;

    while (page <= totalPages) {
      const response = await serviceFn({
        ...getBaseParams(filtrosAtuais),
        page,
        page_size: EXPORT_PAGE_SIZE,
      });

      const payload = response.data || {};
      const items = Array.isArray(payload.items) ? payload.items : [];
      const pagination = payload.pagination || {};
      allItems.push(...items);

      if (Number.isFinite(Number(pagination.total_pages))) {
        totalPages = Number(pagination.total_pages);
      } else if (items.length < EXPORT_PAGE_SIZE) {
        totalPages = page;
      }

      page += 1;
    }

    return allItems;
  };

  const setExportingTab = (tab, value) => {
    setExporting((prev) => ({ ...prev, [tab]: value }));
  };

  const handleExportAlunos = async () => {
    const filtrosAtuais = { ...filtros };
    const dataRef = dayjs(filtrosAtuais.data_referencia).format('YYYY-MM-DD');

    setExportingTab('alunos', true);
    try {
      const items = await fetchAllPages(relatorioService.alunosAtivos, filtrosAtuais);
      if (items.length === 0) {
        message.warning('Nenhum registro encontrado para exportar.');
        return;
      }

      downloadCsv(
        `alunos-ativos_${dataRef}.csv`,
        ['id_aluno', 'nome_aluno', 'responsavel_financeiro', 'unidade', 'turmas'],
        items.map((item) => [
          item.id_aluno,
          item.nome_aluno,
          item.responsavel_financeiro,
          item.unidade,
          item.turmas,
        ])
      );

      message.success(`CSV exportado com ${items.length} registros.`);
    } catch (error) {
      message.error('Erro ao exportar CSV de alunos ativos. Tente novamente.');
      console.error(error);
    } finally {
      setExportingTab('alunos', false);
    }
  };

  const handleExportTurmas = async () => {
    const filtrosAtuais = { ...filtros };
    const dataRef = dayjs(filtrosAtuais.data_referencia).format('YYYY-MM-DD');

    setExportingTab('turmas', true);
    try {
      const items = await fetchAllPages(relatorioService.turmasAbertas, filtrosAtuais);
      if (items.length === 0) {
        message.warning('Nenhum registro encontrado para exportar.');
        return;
      }

      downloadCsv(
        `turmas-abertas_${dataRef}.csv`,
        ['id_turma', 'nome_turma', 'unidade', 'professor', 'alunos_matriculados'],
        items.map((item) => [
          item.id_turma,
          item.nome_turma,
          item.unidade,
          item.professor,
          item.alunos_matriculados,
        ])
      );

      message.success(`CSV exportado com ${items.length} registros.`);
    } catch (error) {
      message.error('Erro ao exportar CSV de turmas abertas. Tente novamente.');
      console.error(error);
    } finally {
      setExportingTab('turmas', false);
    }
  };

  const handleExportMatriculas = async () => {
    const filtrosAtuais = { ...filtros };
    const dataRef = dayjs(filtrosAtuais.data_referencia).format('YYYY-MM-DD');

    setExportingTab('matriculas', true);
    try {
      const items = await fetchAllPages(relatorioService.matriculasAtivasOperacional, filtrosAtuais);
      if (items.length === 0) {
        message.warning('Nenhum registro encontrado para exportar.');
        return;
      }

      downloadCsv(
        `matriculas-ativas_${dataRef}.csv`,
        [
          'ID Matrícula',
          'Aluno',
          'Unidade',
          'Turma Vigente',
          'Ano Letivo',
          'Status',
          'Data de Início',
          'Data de Fim',
          'Tipo de Matrícula',
          'Dia de Vencimento',
          'Forma de Pagamento',
          'Quantidade de Parcelas',
          'Valor Total',
          'Parcela Final',
          'Desconto (%)',
          'Responsável Financeiro',
          'CPF do Responsável',
          'E-mail do Responsável',
          'Telefone do Responsável',
          'Endereço',
          'Número',
          'Complemento',
          'Bairro',
          'Cidade',
          'UF',
          'CEP',
        ],
        items.map((item) => [
          item.id_matricula,
          paraCelulaTexto(item.aluno),
          paraCelulaTexto(item.unidade),
          paraCelulaTexto(item.turma),
          paraCelulaTexto(item.ano_letivo),
          paraCelulaTexto(item.status),
          paraCelulaData(item.data_inicio),
          paraCelulaData(item.data_fim),
          paraCelulaTexto(item.tipo_matricula),
          paraCelulaInteiro(item.dia_vencimento),
          paraCelulaTexto(item.forma_pgto),
          paraCelulaInteiro(item.qtd_parcelas),
          paraCelulaMoeda(item.valor_total),
          paraCelulaMoeda(item.parcela_final),
          paraCelulaPercentual(item.desconto),
          paraCelulaTexto(item.responsavel_nome),
          formatCPF(item.responsavel_cpf),
          paraCelulaTexto(item.responsavel_email),
          paraCelulaTelefone(item.responsavel_telefone),
          paraCelulaTexto(item.responsavel_endereco),
          paraCelulaTexto(item.responsavel_numero),
          paraCelulaTexto(item.responsavel_complemento),
          paraCelulaTexto(item.responsavel_bairro),
          paraCelulaTexto(item.responsavel_cidade),
          paraCelulaTexto(item.responsavel_uf),
          paraCelulaCep(item.responsavel_cep),
        ])
      );

      message.success(`CSV exportado com ${items.length} registros.`);
    } catch (error) {
      message.error('Erro ao exportar CSV de matrículas ativas. Tente novamente.');
      console.error(error);
    } finally {
      setExportingTab('matriculas', false);
    }
  };

  const handleExportProfessores = async () => {
    const filtrosAtuais = { ...filtros };
    const dataRef = dayjs(filtrosAtuais.data_referencia).format('YYYY-MM-DD');

    setExportingTab('professores', true);
    try {
      const items = await fetchAllPages(relatorioService.professoresSimples, filtrosAtuais);
      if (items.length === 0) {
        message.warning('Nenhum registro encontrado para exportar.');
        return;
      }

      downloadCsv(
        `professores-simples_${dataRef}.csv`,
        [
          'id_professor',
          'nome_professor',
          'unidade',
          'turma',
          'media_aulas_por_mes',
        ],
        items.map((item) => [
          item.id_professor,
          item.nome_professor,
          item.unidade,
          item.turma,
          item.media_aulas_por_mes == null ? '-' : Number(item.media_aulas_por_mes).toFixed(2),
        ])
      );

      message.success(`CSV exportado com ${items.length} registros.`);
    } catch (error) {
      message.error('Erro ao exportar CSV de professores. Tente novamente.');
      console.error(error);
    } finally {
      setExportingTab('professores', false);
    }
  };

  const alunosColumns = useMemo(
    () => [
      {
        title: 'ID Aluno',
        dataIndex: 'id_aluno',
        key: 'id_aluno',
        width: 100,
        sorter: (a, b) => (a.id_aluno || 0) - (b.id_aluno || 0),
      },
      {
        title: 'Nome Aluno',
        dataIndex: 'nome_aluno',
        key: 'nome_aluno',
        sorter: (a, b) => (a.nome_aluno || '').localeCompare(b.nome_aluno || ''),
      },
      {
        title: 'Responsável Financeiro',
        dataIndex: 'responsavel_financeiro',
        key: 'responsavel_financeiro',
        sorter: (a, b) =>
          (a.responsavel_financeiro || '').localeCompare(b.responsavel_financeiro || ''),
      },
      {
        title: 'Unidade',
        dataIndex: 'unidade',
        key: 'unidade',
        sorter: (a, b) => (a.unidade || '').localeCompare(b.unidade || ''),
      },
      {
        title: 'Turmas',
        dataIndex: 'turmas',
        key: 'turmas',
        sorter: (a, b) => (a.turmas || '').localeCompare(b.turmas || ''),
      },
    ],
    []
  );

  const turmasColumns = useMemo(
    () => [
      {
        title: 'ID Turma',
        dataIndex: 'id_turma',
        key: 'id_turma',
        width: 100,
        sorter: (a, b) => (a.id_turma || 0) - (b.id_turma || 0),
      },
      {
        title: 'Nome Turma',
        dataIndex: 'nome_turma',
        key: 'nome_turma',
        sorter: (a, b) => (a.nome_turma || '').localeCompare(b.nome_turma || ''),
      },
      {
        title: 'Unidade',
        dataIndex: 'unidade',
        key: 'unidade',
        sorter: (a, b) => (a.unidade || '').localeCompare(b.unidade || ''),
      },
      {
        title: 'Professor',
        dataIndex: 'professor',
        key: 'professor',
        sorter: (a, b) => (a.professor || '').localeCompare(b.professor || ''),
      },
      {
        title: 'Alunos Matriculados',
        dataIndex: 'alunos_matriculados',
        key: 'alunos_matriculados',
        width: 170,
        align: 'center',
        sorter: (a, b) => (a.alunos_matriculados || 0) - (b.alunos_matriculados || 0),
        render: (value) => <Tag color="blue">{value}</Tag>,
      },
    ],
    []
  );

  const matriculasColumns = useMemo(
    () => [
      {
        title: 'Aluno',
        dataIndex: 'aluno',
        key: 'aluno',
        fixed: 'left',
        width: 200,
        sorter: (a, b) => (a.aluno || '').localeCompare(b.aluno || ''),
      },
      {
        title: 'Responsável',
        dataIndex: 'responsavel_nome',
        key: 'responsavel_nome',
        width: 200,
        render: (value) => formatarTextoExibicao(value),
        sorter: (a, b) => (a.responsavel_nome || '').localeCompare(b.responsavel_nome || ''),
      },
      {
        title: 'CPF',
        dataIndex: 'responsavel_cpf',
        key: 'responsavel_cpf',
        width: 140,
        render: (value) => formatCPF(value) || '-',
      },
      {
        title: 'Telefone',
        dataIndex: 'responsavel_telefone',
        key: 'responsavel_telefone',
        width: 150,
        render: (value) => formatarTelefoneExibicao(value),
      },
      {
        title: 'Unidade',
        dataIndex: 'unidade',
        key: 'unidade',
        width: 170,
        sorter: (a, b) => (a.unidade || '').localeCompare(b.unidade || ''),
      },
      {
        title: 'Turma',
        dataIndex: 'turma',
        key: 'turma',
        width: 170,
        sorter: (a, b) => (a.turma || '').localeCompare(b.turma || ''),
      },
      {
        title: 'Tipo',
        dataIndex: 'tipo_matricula',
        key: 'tipo_matricula',
        width: 120,
        render: (value) => capitalizarPalavraExibicao(value),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 110,
        render: (value) => capitalizarPalavraExibicao(value),
      },
      {
        title: 'Início',
        dataIndex: 'data_inicio',
        key: 'data_inicio',
        width: 110,
        render: (value) => formatarDataExibicao(value),
      },
      {
        title: 'Fim',
        dataIndex: 'data_fim',
        key: 'data_fim',
        width: 110,
        render: (value) => formatarDataExibicao(value),
      },
      {
        title: 'Vencimento',
        dataIndex: 'dia_vencimento',
        key: 'dia_vencimento',
        width: 110,
        align: 'right',
        render: (value) => (value === null || value === undefined || value === '' ? '-' : String(value)),
      },
      {
        title: 'Valor Total',
        dataIndex: 'valor_total',
        key: 'valor_total',
        width: 140,
        align: 'right',
        render: (value) => formatarMoedaExibicao(value),
      },
      {
        title: 'Parcela Final',
        dataIndex: 'parcela_final',
        key: 'parcela_final',
        width: 140,
        align: 'right',
        render: (value) => formatarMoedaExibicao(value),
      },
      {
        title: 'Desconto',
        dataIndex: 'desconto',
        key: 'desconto',
        width: 110,
        align: 'right',
        render: (value) => formatarPercentualExibicao(value),
      },
    ],
    []
  );

  const anoLetivoOptions = useMemo(() => {
    const anoRef = dayjs(filtros.data_referencia).year();
    const anos = Array.from({ length: 7 }, (_, idx) => String(anoRef - 3 + idx));

    return [
      { label: 'Ano da data de referência', value: null },
      { label: 'Todos', value: 'TODOS' },
      ...anos.map((ano) => ({ label: ano, value: ano })),
    ];
  }, [filtros.data_referencia]);

  const professoresColumns = useMemo(
    () => [
      {
        title: 'ID Professor',
        dataIndex: 'id_professor',
        key: 'id_professor',
        width: 130,
        sorter: (a, b) => (a.id_professor || 0) - (b.id_professor || 0),
      },
      {
        title: 'Nome Professor',
        dataIndex: 'nome_professor',
        key: 'nome_professor',
        sorter: (a, b) =>
          (a.nome_professor || '').localeCompare(b.nome_professor || ''),
      },
      {
        title: 'Unidade',
        dataIndex: 'unidade',
        key: 'unidade',
        sorter: (a, b) => (a.unidade || '').localeCompare(b.unidade || ''),
      },
      {
        title: 'Turma',
        dataIndex: 'turma',
        key: 'turma',
        sorter: (a, b) => (a.turma || '').localeCompare(b.turma || ''),
      },
      {
        title: 'Média Aulas/Mês',
        dataIndex: 'media_aulas_por_mes',
        key: 'media_aulas_por_mes',
        width: 160,
        align: 'right',
        sorter: (a, b) =>
          Number(a.media_aulas_por_mes ?? -1) - Number(b.media_aulas_por_mes ?? -1),
        render: (value) => (value == null ? '-' : Number(value).toFixed(2)),
      },
    ],
    []
  );

  if (permissaoLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spin size="large" tip="Validando permissões..." />
      </div>
    );
  }

  if (!isAdmin && !podeVisualizar) {
    return <Navigate to="/acesso-negado" replace />;
  }

  return (
    <div>
      <Card title="Relatórios Operacionais">
        <Form layout="vertical">
          <Row gutter={16} align="bottom" style={{ marginBottom: 8 }}>
            <Col xs={24} sm={12} md={6}>
              <Form.Item label="Unidade / Escola" style={{ marginBottom: 0 }}>
                <Select
                  allowClear
                  placeholder="Todas as unidades"
                  style={{ width: '100%' }}
                  value={filtros.id_unidade}
                  onChange={(value) =>
                    setFiltros((prev) => ({ ...prev, id_unidade: value || null }))
                  }
                  options={unidades.map((unidade) => ({
                    label: unidade.nome_fantasia,
                    value: unidade.id,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item label="Professor" style={{ marginBottom: 0 }}>
                <Select
                  allowClear
                  placeholder="Todos os professores"
                  style={{ width: '100%' }}
                  value={filtros.id_professor}
                  onChange={(value) =>
                    setFiltros((prev) => ({ ...prev, id_professor: value || null }))
                  }
                  options={professores.map((professor) => ({
                    label: professor.nome,
                    value: professor.id,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item label="Data de referência" style={{ marginBottom: 0 }}>
                <DatePicker
                  placeholder="Data de referência"
                  format="DD/MM/YYYY"
                  style={{ width: '100%' }}
                  value={filtros.data_referencia}
                  onChange={(value) =>
                    setFiltros((prev) => ({
                      ...prev,
                      data_referencia: value || dayjs(),
                    }))
                  }
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item label="Ano Letivo" style={{ marginBottom: 0 }}>
                <Select
                  placeholder="Ano da data de referência"
                  style={{ width: '100%' }}
                  value={filtros.ano_letivo}
                  onChange={(value) =>
                    setFiltros((prev) => ({ ...prev, ano_letivo: value ?? null }))
                  }
                  options={anoLetivoOptions}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Space>
                <Button type="primary" icon={<SearchOutlined />} onClick={handleBuscar}>
                  Aplicar filtros
                </Button>
                <Button onClick={handleLimparFiltros}>Limpar filtros</Button>
              </Space>
            </Col>
          </Row>
        </Form>

        <Divider style={{ marginTop: 4 }} />

        <Tabs
          items={[
            {
              key: 'alunos-ativos',
              label: 'Alunos Ativos',
              children: (
                <>
                  <Row justify="end" style={{ marginBottom: 12 }}>
                    <Col>
                      <Button
                        icon={<DownloadOutlined />}
                        loading={exporting.alunos}
                        disabled={exporting.alunos || alunosPagination.total === 0}
                        onClick={handleExportAlunos}
                      >
                        {exporting.alunos ? 'Exportando...' : 'Exportar CSV'}
                      </Button>
                    </Col>
                  </Row>
                  <Table
                    rowKey="id_aluno"
                    columns={alunosColumns}
                    dataSource={alunosAtivos}
                    loading={loadingAlunos}
                    pagination={{
                      current: alunosPagination.page,
                      pageSize: alunosPagination.page_size,
                      total: alunosPagination.total,
                      showSizeChanger: true,
                      pageSizeOptions: ['20', '50', '100'],
                    }}
                    onChange={(tablePagination) => {
                      buscarAlunosAtivos(tablePagination.current, tablePagination.pageSize, filtros);
                    }}
                  />
                </>
              ),
            },
            {
              key: 'turmas-abertas',
              label: 'Turmas Abertas',
              children: (
                <>
                  <Row justify="end" style={{ marginBottom: 12 }}>
                    <Col>
                      <Button
                        icon={<DownloadOutlined />}
                        loading={exporting.turmas}
                        disabled={exporting.turmas || turmasPagination.total === 0}
                        onClick={handleExportTurmas}
                      >
                        {exporting.turmas ? 'Exportando...' : 'Exportar CSV'}
                      </Button>
                    </Col>
                  </Row>
                  <Table
                    rowKey="id_turma"
                    columns={turmasColumns}
                    dataSource={turmasAbertas}
                    loading={loadingTurmas}
                    pagination={{
                      current: turmasPagination.page,
                      pageSize: turmasPagination.page_size,
                      total: turmasPagination.total,
                      showSizeChanger: true,
                      pageSizeOptions: ['20', '50', '100'],
                    }}
                    onChange={(tablePagination) => {
                      buscarTurmasAbertas(tablePagination.current, tablePagination.pageSize, filtros);
                    }}
                  />
                </>
              ),
            },
            {
              key: 'matriculas-ativas',
              label: 'Matrículas',
              children: (
                <>
                  <Row justify="end" style={{ marginBottom: 12 }}>
                    <Col>
                      <Button
                        icon={<DownloadOutlined />}
                        loading={exporting.matriculas}
                        disabled={exporting.matriculas || matriculasPagination.total === 0}
                        onClick={handleExportMatriculas}
                      >
                        {exporting.matriculas ? 'Exportando...' : 'Exportar CSV'}
                      </Button>
                    </Col>
                  </Row>
                  <Table
                    rowKey="id_matricula"
                    columns={matriculasColumns}
                    dataSource={matriculasAtivas}
                    loading={loadingMatriculas}
                    scroll={{ x: 1700, y: 520 }}
                    locale={{ emptyText: 'Nenhuma matrícula encontrada para os filtros selecionados.' }}
                    pagination={{
                      current: matriculasPagination.page,
                      pageSize: matriculasPagination.page_size,
                      total: matriculasPagination.total,
                      showSizeChanger: true,
                      pageSizeOptions: ['20', '50', '100'],
                      showTotal: (total) => `${total} matrícula${total === 1 ? '' : 's'} encontrada${total === 1 ? '' : 's'}`,
                    }}
                    onChange={(tablePagination) => {
                      buscarMatriculasAtivas(
                        tablePagination.current,
                        tablePagination.pageSize,
                        filtros
                      );
                    }}
                  />
                </>
              ),
            },
            {
              key: 'professores-simples',
              label: 'Professores',
              children: (
                <>
                  <Row justify="end" style={{ marginBottom: 12 }}>
                    <Col>
                      <Button
                        icon={<DownloadOutlined />}
                        loading={exporting.professores}
                        disabled={exporting.professores || professoresPagination.total === 0}
                        onClick={handleExportProfessores}
                      >
                        {exporting.professores ? 'Exportando...' : 'Exportar CSV'}
                      </Button>
                    </Col>
                  </Row>
                  <Table
                    rowKey={(row) => `${row.id_professor}-${row.unidade}-${row.turma}`}
                    columns={professoresColumns}
                    dataSource={professoresSimples}
                    loading={loadingProfessores}
                    pagination={{
                      current: professoresPagination.page,
                      pageSize: professoresPagination.page_size,
                      total: professoresPagination.total,
                      showSizeChanger: true,
                      pageSizeOptions: ['20', '50', '100'],
                    }}
                    onChange={(tablePagination) => {
                      buscarProfessoresSimples(
                        tablePagination.current,
                        tablePagination.pageSize,
                        filtros
                      );
                    }}
                  />
                </>
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
};

export default RelatoriosOperacionais;
